import classNames from 'classnames'
import React, { useCallback, useEffect, useRef } from 'react'

import styles from './ChartResizeHandle.module.scss'

type ChartResizeHandleProps = {
  /** current chart height in px. */
  value: number
  /** called during drag with the proposed new chart height (already clamped) */
  onChange: (next: number) => void
  /** called once when drag starts-caller can read the wrapper height if needed */
  onDragStart?: () => void
  /** called once when drag ends-good place to persist the final value */
  onDragEnd?: (final: number) => void
  /** min allowed chart height in px */
  min: number
  /** max allowed chart height in px */
  max: number
  /** keyboard nudge step in px (default 24) */
  step?: number
  'aria-label'?: string
  className?: string
}

/**
 * horzontal resize handle placed between two stacked blocks
 * drag up grows the bottom block (chart), dragging down shrinks it
 * uses pointer capture + rAF throttling so the TV iframe doesn't thrash
 * keyboard-accessible via ArrowUp / ArrowDown / Home / End with the `slider` role
 */
export const ChartResizeHandle: React.FC<ChartResizeHandleProps> = ({
  value,
  onChange,
  onDragStart,
  onDragEnd,
  min,
  max,
  step = 24,
  'aria-label': ariaLabel = 'Resize chart',
  className,
}) => {
  const rafRef = useRef<number | null>(null)
  const startYRef = useRef(0)
  const startValueRef = useRef(0)
  const latestValueRef = useRef(value)
  const isDraggingRef = useRef(false)

  useEffect(() => {
    latestValueRef.current = value
  }, [value])

  const clamp = useCallback((n: number) => Math.min(max, Math.max(min, n)), [min, max])

  const cancelRaf = () => {
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current)
      rafRef.current = null
    }
  }

  const handlePointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      // ignore non-primary buttons (keeps right-click menu working)
      if (event.button !== 0) return
      event.preventDefault()

      const target = event.currentTarget
      target.setPointerCapture(event.pointerId)

      startYRef.current = event.clientY
      startValueRef.current = latestValueRef.current
      isDraggingRef.current = true

      document.body.style.cursor = 'ns-resize'
      document.body.style.userSelect = 'none'

      onDragStart?.()
    },
    [onDragStart]
  )

  const handlePointerMove = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (!isDraggingRef.current) return

      const delta = event.clientY - startYRef.current
      // dragging up (negative delta) should GROW the chart
      const proposed = clamp(startValueRef.current - delta)

      if (proposed === latestValueRef.current) return

      cancelRaf()
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null
        latestValueRef.current = proposed
        onChange(proposed)
      })
    },
    [clamp, onChange]
  )

  const endDrag = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (!isDraggingRef.current) return
      isDraggingRef.current = false

      const target = event.currentTarget
      if (target.hasPointerCapture(event.pointerId)) {
        target.releasePointerCapture(event.pointerId)
      }

      document.body.style.cursor = ''
      document.body.style.userSelect = ''

      cancelRaf()
      onDragEnd?.(latestValueRef.current)
    },
    [onDragEnd]
  )

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      let next: number | null = null
      switch (event.key) {
        case 'ArrowUp':
          next = clamp(latestValueRef.current + step)
          break
        case 'ArrowDown':
          next = clamp(latestValueRef.current - step)
          break
        case 'Home':
          next = min
          break
        case 'End':
          next = max
          break
      }

      if (next != null && next !== latestValueRef.current) {
        event.preventDefault()
        latestValueRef.current = next
        onChange(next)
        onDragEnd?.(next)
      }
    },
    [clamp, max, min, onChange, onDragEnd, step]
  )

  useEffect(() => cancelRaf, [])

  return (
    <div
      className={classNames(styles.handle, className)}
      role='slider'
      tabIndex={0}
      aria-label={ariaLabel}
      aria-orientation='horizontal'
      aria-valuemin={min}
      aria-valuemax={max}
      aria-valuenow={Math.round(value)}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onKeyDown={handleKeyDown}
    >
      <span className={styles.grip} aria-hidden />
    </div>
  )
}
